import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogIn,
  UserPlus,
  Mail,
  KeyRound,
  User as UserIcon,
  ArrowRight,
  Loader2,
  Eye,
  EyeOff,
  MailCheck,
  Send,
  Check,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const TABS = [
  { key: "login", label: "Accedi", icon: LogIn },
  { key: "register", label: "Registrati", icon: UserPlus },
];

function PasswordField({ id, value, onChange, autoComplete, placeholder, minLength, testId }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
      <input
        data-testid={testId}
        id={id}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        required
        minLength={minLength}
        value={value}
        onChange={onChange}
        className="w-full pl-10 pr-11 py-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={`${testId}-toggle`}
        aria-label={visible ? "Nascondi password" : "Mostra password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function AuthPage() {
  const { isAuthenticated, isLoading, login, register, resendVerification } = useAuth();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(null); // email string
  const [resendMsg, setResendMsg] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [registered, setRegistered] = useState(null); // email string after successful register
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsVersion, setTermsVersion] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Carica la versione corrente dei Termini per poterla marcare come
  // "vista dall'utente" al momento dell'accettazione.
  useEffect(() => {
    let mounted = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/terms/current`)
      .then((r) => {
        if (mounted) setTermsVersion(r.data?.terms_version || null);
      })
      .catch(() => {
        /* fallback: il backend userà comunque la sua current version */
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Post-login redirect target: `?next=` query param (usato dai deep-link
  // esterni come /prodotti/.../libro) o `state.from` (fallback existing).
  const nextParam = new URLSearchParams(location.search).get("next");
  const postLoginTarget = nextParam || location.state?.from || "/area-personale";

  if (isAuthenticated) {
    return <Navigate to={postLoginTarget} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerification(null);
    setResendMsg("");
    setBusy(true);
    try {
      if (tab === "login") {
        await login(email.trim(), password);
        navigate(postLoginTarget, { replace: true });
      } else {
        if (password.length < 8) {
          throw new Error("La password deve avere almeno 8 caratteri.");
        }
        if (!termsAccepted) {
          throw new Error(
            "Per completare la registrazione devi accettare i Termini e Condizioni."
          );
        }
        await register(email.trim(), password, name.trim() || null, {
          terms_accepted: true,
          terms_version: termsVersion || undefined,
        });
        setRegistered(email.trim());
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend returns object detail for unverified email → show resend UI
      if (
        err?.response?.status === 403 &&
        detail &&
        typeof detail === "object" &&
        detail.code === "email_not_verified"
      ) {
        setNeedsVerification(detail.email || email.trim());
        setError(detail.message || "Devi confermare la tua email prima di accedere.");
      } else {
        const msg = err?.response
          ? formatApiErrorDetail(detail)
          : err.message || "Errore sconosciuto";
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    if (!needsVerification) return;
    setResendBusy(true);
    setResendMsg("");
    try {
      const data = await resendVerification(needsVerification);
      setResendMsg(data?.message || "Email inviata. Controlla la casella di posta.");
    } catch (e) {
      setResendMsg(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setResendBusy(false);
    }
  };

  // Post-registration success screen
  if (registered) {
    return (
      <div className="px-5 sm:px-8 lg:px-12 py-20 max-w-xl mx-auto" data-testid="auth-registered">
        <PageHeader
          eyebrow="Registrazione completata"
          title="Controlla la tua email"
          description="Ti abbiamo inviato un link di conferma. Clicca sul link per attivare il tuo account."
          testId="registered-header"
        />
        <div className="glass-card p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center mx-auto mb-6">
            <MailCheck className="w-7 h-7 text-emerald-300" />
          </div>
          <div className="text-[11px] tracking-[0.24em] uppercase text-white/40 mb-2">
            Email inviata a
          </div>
          <div className="text-lg text-white/90 font-light mb-8 break-all" data-testid="registered-email">
            {registered}
          </div>
          <p className="text-sm text-white/55 font-light leading-relaxed mb-8">
            Il link è valido per 24 ore. Se non trovi l'email, controlla la
            cartella spam o richiedi un nuovo invio.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={async () => {
                setResendBusy(true);
                setResendMsg("");
                try {
                  const data = await resendVerification(registered);
                  setResendMsg(data?.message || "Email reinviata.");
                } catch (e) {
                  setResendMsg(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
                } finally {
                  setResendBusy(false);
                }
              }}
              disabled={resendBusy}
              data-testid="registered-resend"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/70 hover:text-white hover:border-white/30 transition-all disabled:opacity-50"
            >
              {resendBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Reinvia email
            </button>
            <button
              type="button"
              onClick={() => {
                setRegistered(null);
                setTab("login");
                setPassword("");
              }}
              data-testid="registered-back-to-login"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all"
            >
              <Check className="w-3.5 h-3.5" />
              Vai al login
            </button>
          </div>
          {resendMsg && (
            <div
              data-testid="registered-resend-msg"
              className="mt-6 text-[12px] text-white/60 bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3"
            >
              {resendMsg}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-20 max-w-xl mx-auto" data-testid="auth-page">
      <PageHeader
        eyebrow="Area riservata"
        title={tab === "login" ? "Accedi al tuo account" : "Crea il tuo account"}
        description={
          tab === "login"
            ? "Riprendi il tuo percorso TIME Inside da dove eri rimasto."
            : "Un solo account per accedere a TIME Watch e a tutta la tua area personale."
        }
        testId="auth-header"
      />

      <div
        className="glass-card p-6 sm:p-8"
        data-testid="auth-card"
      >
        {/* Tabs */}
        <div
          role="tablist"
          className="grid grid-cols-2 gap-1 p-1 mb-8 rounded-xl bg-white/[0.03] border border-white/5"
        >
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              data-testid={`auth-tab-${key}`}
              onClick={() => {
                setTab(key);
                setError("");
                setNeedsVerification(null);
                setResendMsg("");
              }}
              className={[
                "flex items-center justify-center gap-2 py-2.5 rounded-lg text-[12px] tracking-[0.22em] uppercase transition-all duration-300",
                tab === key
                  ? "bg-white/[0.08] text-white border border-white/10"
                  : "text-white/40 hover:text-white/70",
              ].join(" ")}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
          {tab === "register" && (
            <label className="block">
              <span className="block text-[11px] tracking-[0.24em] uppercase text-white/40 mb-2">
                Nome (opzionale)
              </span>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  data-testid="auth-input-name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
                  placeholder="Come vuoi essere chiamato"
                />
              </div>
            </label>
          )}

          <label className="block">
            <span className="block text-[11px] tracking-[0.24em] uppercase text-white/40 mb-2">
              Email
            </span>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                data-testid="auth-input-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
                placeholder="tu@esempio.com"
              />
            </div>
          </label>

          <label className="block">
            <span className="block text-[11px] tracking-[0.24em] uppercase text-white/40 mb-2">
              Password
            </span>
            <PasswordField
              id="auth-password"
              testId="auth-input-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={tab === "login" ? "current-password" : "new-password"}
              minLength={tab === "register" ? 8 : 1}
              placeholder={tab === "register" ? "Almeno 8 caratteri" : "La tua password"}
            />
          </label>

          {error && (
            <div
              data-testid="auth-error"
              className="text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
            >
              {error}
            </div>
          )}

          {tab === "register" && (
            <label
              className="flex items-start gap-3 cursor-pointer group"
              data-testid="auth-terms-label"
            >
              <input
                type="checkbox"
                data-testid="auth-terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border border-white/20 bg-black/40 text-emerald-400 focus:ring-emerald-400/40 focus:ring-offset-0 accent-emerald-400 cursor-pointer"
              />
              <span className="text-[12px] leading-relaxed text-white/60 group-hover:text-white/75 transition-colors">
                Dichiaro di aver letto e accetto i{" "}
                <Link
                  to="/termini"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="auth-terms-link"
                  className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2 decoration-emerald-400/40 hover:decoration-emerald-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  Termini e Condizioni
                </Link>
                .
              </span>
            </label>
          )}

          {needsVerification && (
            <div
              data-testid="auth-verify-block"
              className="rounded-lg border border-amber-400/25 bg-amber-500/[0.06] px-4 py-3 space-y-3"
            >
              <div className="text-[12px] text-amber-200/90 leading-relaxed">
                Il tuo account non è ancora verificato. Controlla la casella di
                posta di <span className="text-amber-100 font-medium">{needsVerification}</span> o
                richiedi un nuovo link.
              </div>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendBusy}
                data-testid="auth-resend-verification"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/40 text-[11px] tracking-[0.24em] uppercase text-amber-100 hover:bg-amber-400/10 transition-all disabled:opacity-50"
              >
                {resendBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Invia nuovamente e-mail di conferma
              </button>
              {resendMsg && (
                <div
                  data-testid="auth-resend-msg"
                  className="text-[12px] text-white/60"
                >
                  {resendMsg}
                </div>
              )}
            </div>
          )}

          <button
            data-testid="auth-submit"
            type="submit"
            disabled={busy || isLoading || (tab === "register" && !termsAccepted)}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-400/90 text-black text-[12px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {tab === "login" ? "Accedi" : "Crea account"}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          {tab === "login" && (
            <div className="text-center pt-2">
              <Link
                to="/auth/password-dimenticata"
                data-testid="auth-forgot-link"
                className="text-[11px] tracking-[0.22em] uppercase text-white/40 hover:text-white/70 transition-colors"
              >
                Password dimenticata?
              </Link>
            </div>
          )}
        </form>
      </div>

      <p className="mt-6 text-center text-[11px] tracking-[0.24em] uppercase text-white/25">
        I tuoi dati TIME Watch sono conservati in modo sicuro e privato.
      </p>
    </div>
  );
}
